import { useEffect, useState } from "react";
import { useGetIdentity, useNotify } from "ra-core";
import { ShieldCheck, Plus, Search, Edit2, Trash2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getSupabaseClient } from "../providers/supabase/supabase";

type QCInspection = {
  id: number;
  purchase_order_id: number | null;
  inspection_date: string | null;
  inspector_name: string | null;
  inspection_company: string | null;
  aql_level: string | null;
  result: string;
  defect_rate: number | null;
  report_url: string | null;
  notes: string | null;
  created_at: string;
  purchase_orders?: { po_number: string | null; suppliers?: { name: string } | null } | null;
};

type PO = { id: number; po_number: string | null; supplier_name: string };

const RESULT_BADGE: Record<string, string> = {
  pass: "bg-green-100 text-green-700",
  conditional_pass: "bg-yellow-100 text-yellow-700",
  fail: "bg-red-100 text-red-700",
};

const AQL_LEVELS = ["AQL 1.5", "AQL 2.5", "AQL 4.0"];

const emptyForm = () => ({
  purchase_order_id: "",
  inspection_date: new Date().toISOString().slice(0, 10),
  inspector_name: "",
  inspection_company: "",
  aql_level: "AQL 2.5",
  result: "pass",
  defect_rate: "",
  report_url: "",
  rework_required: "false",
  notes: "",
});

const QCInspectionsPage = () => {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const [records, setRecords] = useState<QCInspection[]>([]);
  const [pos, setPos] = useState<PO[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [resultFilter, setResultFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<QCInspection | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = () => {
    const supabase = getSupabaseClient();
    Promise.all([
      supabase
        .from("quality_inspections")
        .select("*, purchase_orders(po_number, suppliers(name))")
        .order("created_at", { ascending: false }),
      supabase
        .from("purchase_orders")
        .select("id, po_number, suppliers(name)")
        .order("created_at", { ascending: false })
        .limit(200),
    ]).then(([qcRes, poRes]) => {
      setRecords(qcRes.data as QCInspection[] ?? []);
      setPos(
        (poRes.data ?? []).map((p: any) => ({
          id: p.id,
          po_number: p.po_number,
          supplier_name: p.suppliers?.name ?? "—",
        }))
      );
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (r: QCInspection) => {
    setEditing(r);
    setForm({
      purchase_order_id: r.purchase_order_id?.toString() ?? "",
      inspection_date: r.inspection_date ?? new Date().toISOString().slice(0, 10),
      inspector_name: r.inspector_name ?? "",
      inspection_company: r.inspection_company ?? "",
      aql_level: r.aql_level ?? "AQL 2.5",
      result: r.result,
      defect_rate: r.defect_rate?.toString() ?? "",
      report_url: r.report_url ?? "",
      rework_required: "false",
      notes: r.notes ?? "",
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const supabase = getSupabaseClient();
    const payload = {
      purchase_order_id: form.purchase_order_id ? parseInt(form.purchase_order_id) : null,
      inspection_date: form.inspection_date || null,
      inspector_name: form.inspector_name || null,
      inspection_company: form.inspection_company || null,
      aql_level: form.aql_level || null,
      result: form.result,
      defect_rate: form.defect_rate ? parseFloat(form.defect_rate) : null,
      report_url: form.report_url || null,
      notes: form.notes || null,
      updated_at: new Date().toISOString(),
    };
    if (editing) {
      const { error } = await supabase.from("quality_inspections").update(payload).eq("id", editing.id);
      if (error) { notify(error.message, { type: "error" }); setSaving(false); return; }
      notify("Updated", { type: "info" });
    } else {
      const { error } = await supabase.from("quality_inspections").insert({ ...payload, created_by: identity?.id ?? null });
      if (error) { notify(error.message, { type: "error" }); setSaving(false); return; }
      notify("Inspection recorded", { type: "info" });
      // Update PO status to qc_passed or in_production based on result
      if (payload.purchase_order_id) {
        const newStatus = payload.result === "pass" || payload.result === "conditional_pass" ? "qc_passed" : "in_production";
        await supabase.from("purchase_orders").update({ status: newStatus, updated_at: new Date().toISOString() }).eq("id", payload.purchase_order_id);
      }
    }
    setSaving(false); setShowForm(false); load();
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this inspection record?")) return;
    await getSupabaseClient().from("quality_inspections").delete().eq("id", id);
    notify("Deleted", { type: "info" }); load();
  };

  const filtered = records.filter(r => {
    if (resultFilter !== "all" && r.result !== resultFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (r.purchase_orders?.po_number ?? "").toLowerCase().includes(q) ||
        (r.purchase_orders?.suppliers?.name ?? "").toLowerCase().includes(q) ||
        (r.inspector_name ?? "").toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="p-6 space-y-5 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck className="w-6 h-6" /> Quality Inspections</h1>
        <Button size="sm" onClick={openCreate} className="gap-1"><Plus className="w-3.5 h-3.5" /> New Inspection</Button>
      </div>

      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
          <Input className="pl-8 h-8 text-sm" placeholder="PO#, supplier, inspector..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex border rounded-lg overflow-hidden">
          {["all", "pass", "conditional_pass", "fail"].map(s => (
            <button key={s} onClick={() => setResultFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${resultFilter === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {s === "all" ? "All" : s === "conditional_pass" ? "Cond. Pass" : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded-lg" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <ShieldCheck className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>No inspection records. <button onClick={openCreate} className="text-primary underline">Add first inspection</button></p>
        </div>
      ) : (
        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-xs text-muted-foreground uppercase">
                <th className="text-left px-4 py-2.5">Date</th>
                <th className="text-left px-4 py-2.5">PO / Supplier</th>
                <th className="text-left px-4 py-2.5 hidden md:table-cell">Inspector</th>
                <th className="text-left px-4 py-2.5 hidden md:table-cell">AQL</th>
                <th className="text-right px-4 py-2.5 hidden lg:table-cell">Defect Rate</th>
                <th className="text-center px-4 py-2.5">Result</th>
                <th className="px-4 py-2.5 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-t hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3 text-muted-foreground">{r.inspection_date ? new Date(r.inspection_date).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs">{r.purchase_orders?.po_number ?? `PO-${r.purchase_order_id}`}</div>
                    <div className="text-xs text-muted-foreground">{r.purchase_orders?.suppliers?.name ?? "—"}</div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div>{r.inspector_name ?? "—"}</div>
                    {r.inspection_company && <div className="text-xs text-muted-foreground">{r.inspection_company}</div>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{r.aql_level ?? "—"}</td>
                  <td className="px-4 py-3 text-right hidden lg:table-cell tabular-nums">
                    {r.defect_rate != null ? `${r.defect_rate.toFixed(2)}%` : "—"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${RESULT_BADGE[r.result] ?? ""}`}>
                      {r.result === "conditional_pass" ? "Cond. Pass" : r.result.charAt(0).toUpperCase() + r.result.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      {r.report_url && (
                        <a href={r.report_url} target="_blank" rel="noopener noreferrer"
                          className="p-1 text-muted-foreground hover:text-foreground"><ExternalLink className="w-3.5 h-3.5" /></a>
                      )}
                      <button onClick={() => openEdit(r)} className="p-1 text-muted-foreground hover:text-foreground"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(r.id)} className="p-1 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={o => !o && setShowForm(false)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Inspection" : "New QC Inspection"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Purchase Order</Label>
              <Select value={form.purchase_order_id} onValueChange={v => set("purchase_order_id", v)}>
                <SelectTrigger className="h-8"><SelectValue placeholder="Select PO..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {pos.map(p => <SelectItem key={p.id} value={p.id.toString()}>{p.po_number ?? `PO-${p.id}`} — {p.supplier_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Inspection Date</Label>
              <Input className="h-8 text-sm" type="date" value={form.inspection_date} onChange={e => set("inspection_date", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>AQL Standard</Label>
              <Select value={form.aql_level} onValueChange={v => set("aql_level", v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>{AQL_LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Inspector Name</Label>
              <Input className="h-8 text-sm" value={form.inspector_name} onChange={e => set("inspector_name", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Inspection Company</Label>
              <Input className="h-8 text-sm" placeholder="SGS / Bureau Veritas..." value={form.inspection_company} onChange={e => set("inspection_company", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Result</Label>
              <Select value={form.result} onValueChange={v => set("result", v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pass">Pass</SelectItem>
                  <SelectItem value="conditional_pass">Conditional Pass</SelectItem>
                  <SelectItem value="fail">Fail</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Defect Rate (%)</Label>
              <Input className="h-8 text-sm" type="number" step="0.01" placeholder="1.50" value={form.defect_rate} onChange={e => set("defect_rate", e.target.value)} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Report URL</Label>
              <Input className="h-8 text-sm" type="url" placeholder="https://..." value={form.report_url} onChange={e => set("report_url", e.target.value)} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Textarea className="text-sm resize-none" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : (editing ? "Update" : "Submit")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QCInspectionsPage;
